import { Link, useParams } from 'react-router-dom';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import NotFound from '../components/NotFound.jsx';
import Seo from '../components/Seo.jsx';
import { Eyebrow } from '../components/Section.jsx';
import { formatReviewDate, getGuide, guideJsonLd } from '../lib/guides.js';

// One evergreen guide. Structure comes entirely from src/lib/guides.js — the
// prerender renders the same data into static HTML, so anything added here
// that isn't in the data model won't reach non-JS crawlers.

export default function Guide() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  if (!guide) return <GuideNotFound />;

  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo title={guide.metaTitle} description={guide.metaDescription} jsonLd={guideJsonLd(guide)} />
      <Header />
      {/* The article runs at the narrower long-form measure while the masthead
          stays at `brief` width, so the header keeps matching every other page. */}
      <article className="mx-auto max-w-read">
        <div className="mt-8 border-b border-navy-100 pb-6">
          <Eyebrow text="Charleston trip guide" />
          <h1 className="mt-2 font-display text-3xl font-semibold text-navy-900 sm:text-4xl">
            {guide.title}
          </h1>
          <p className="mt-2 text-xs uppercase tracking-wider text-navy-500">
            Last reviewed {formatReviewDate(guide.lastReviewed)}
          </p>
          <div className="prose-navy mt-5 space-y-4 text-navy-800">
            {guide.intro.map((text, i) => (
              // Editorial deck: the opening paragraph reads a step larger. text-xl,
              // not text-lg: against the 17px serif body, 18px was barely a step.
              <p key={i} className={i === 0 ? 'text-xl text-navy-900' : ''}>
                {text}
              </p>
            ))}
          </div>
        </div>

        {guide.sections.map((section) => (
          <section key={section.heading} className="border-t border-navy-100 py-6 first:border-t-0">
            <h2 className="mb-3 font-display text-xl font-semibold text-navy-900 sm:text-2xl">
              {section.heading}
            </h2>
            {section.paragraphs && (
              <div className="prose-navy space-y-4 text-navy-800">
                {section.paragraphs.map((text, i) => (
                  <p key={i}>{text}</p>
                ))}
              </div>
            )}
            {section.list && (
              <ul className="prose-navy mt-3 list-disc space-y-2 pl-5 text-navy-800">
                {section.list.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
            {section.callout && <Callout callout={section.callout} />}
          </section>
        ))}

        {guide.faq?.length > 0 && (
          <section className="border-t border-navy-100 py-6">
            <h2 className="mb-3 font-display text-xl font-semibold text-navy-900 sm:text-2xl">
              Questions, answered
            </h2>
            <dl>
              {guide.faq.map((item) => (
                <div key={item.q} className="mt-4 first:mt-0">
                  {/* text-lg: the answer is 17px serif, and the question must not read smaller. */}
                  <dt className="font-display text-lg font-semibold text-navy-900">{item.q}</dt>
                  <dd className="prose-navy mt-1 text-navy-700">{item.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {guide.related?.length > 0 && (
          <div className="border-t border-navy-100 py-6">
            <Eyebrow text="Keep reading" />
            <ul className="mt-2 space-y-1">
              {guide.related.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-navy-900 underline">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}

// The cross-reference box: the guide's quiet conversion/linking mechanism.
function Callout({ callout }) {
  return (
    <div className="mt-4 rounded-xl border border-navy-100 border-l-2 border-l-cream-500 bg-cream-100/60 px-5 py-4 text-sm">
      <p className="leading-relaxed text-navy-800">{callout.text}</p>
      {callout.links?.length > 0 && (
        <p className="mt-2">
          {callout.links.map((link, i) => (
            <span key={link.to}>
              {i > 0 && ' · '}
              <Link to={link.to} className="font-medium text-navy-900 underline">
                {link.label}
              </Link>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

// Unknown slug or a draft: identical 404, so drafts leak nothing.
function GuideNotFound() {
  return (
    <NotFound
      title="No guide here."
      seoTitle="Not found · Charleston.Vacations"
    >
      <Link to="/guides" className="text-navy-700 underline">All guides</Link>
      {' · '}
      <Link to="/" className="text-navy-700 underline">Today's brief</Link>
    </NotFound>
  );
}
